/**
 * Building an `ItemVerdict`. One place, so every item type produces the same
 * shape and the three states of D32 stay distinguishable:
 *
 *   answered: false                  a blank
 *   answered: true,  correct: null   nobody has judged it yet
 *   answered: true,  correct: false  wrong
 */

import { pick } from '@cet/asset-contract';
import type { FeedbackDoc, ItemVerdict, Locale, VerdictPart } from '@cet/asset-contract';

/**
 * No response, or one that normalizes to nothing. `correct: false` because it
 * earns no points and a consumer computing a percentage must count it — the
 * blankness is carried by `answered`, not by `correct`.
 *
 * No feedback: there is nothing to give feedback about, and in an exam the text
 * would not be present anyway.
 */
export function unanswered(itemId: string, max: number): ItemVerdict {
  return { itemId, answered: false, correct: false, points: 0, max };
}

/**
 * `awarded` is passed explicitly rather than derived from `correct`, because partial credit
 * exists: a three-of-four ordering is `correct: false` and yet earns points. Deriving
 * points from correctness would have made partial credit inexpressible.
 */
export function graded(
  itemId: string,
  score: { awarded: number; max: number },
  feedback: FeedbackDoc | undefined,
  correct: boolean,
  locale: Locale,
  /**
   * Per-part detail, for the types that have parts. Passed in rather than derived, because only
   * the item's own scorer knows what a "part" is for it — a position in a sequence, a left-hand
   * entry in a mapping — and this function must not learn about item types to build one.
   */
  parts?: readonly VerdictPart[],
): ItemVerdict {
  const verdict: ItemVerdict = {
    itemId,
    answered: true,
    correct,
    points: score.awarded,
    max: score.max,
    ...(parts === undefined ? {} : { parts }),
  };

  const message = messageFor(feedback, correct, locale);
  // Spread rather than assign `message: undefined`, which
  // exactOptionalPropertyTypes rejects — and rightly: an absent message and a
  // message that is explicitly nothing should not be the same value on the wire.
  return message === undefined ? verdict : { ...verdict, message };
}

/**
 * Feedback text comes from the scheme (D21), so it is present exactly when the
 * scheme is — practice mode. In an exam the scorer runs on the server with the
 * scheme, and the host chooses whether to pass the message back.
 */
function messageFor(
  feedback: FeedbackDoc | undefined,
  correct: boolean,
  locale: Locale,
): string | undefined {
  if (feedback === undefined) return undefined;

  const text = correct ? feedback.correct : feedback.incorrect;
  if (text === undefined) return undefined;

  const resolved = pick(text, locale);
  return resolved === '' ? undefined : resolved;
}
